import { Component, computed, DestroyRef, effect, inject, input, linkedSignal, output, signal } from '@angular/core';
import { DealOptionsService } from '../../../../services/deal-options.service';
import { DealOption, UpdateDealAddendumPayload } from '../../../../models/deal-options-view.models';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { BtnLoaderDirective, CurrencyFormatPipe, DropdownData, GenericDropdownComponent } from '@zayan-erp/shared';
import { DatePipe } from '@angular/common';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { SkeletonModule } from '@openng/optimus-ui/skeleton';
import { MessageModule } from '@openng/optimus-ui/message';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { BuilderReferenceService } from 'libs/home-building/src/lib/services/builder-reference.service';
import { map } from 'rxjs';
import { UserAuthenticationService } from 'libs/administration/src/lib/services/user-authentication.service';
import { UserModuleRole } from '@zayan-erp/administration';
import { ToastrService } from 'ngx-toastr';
import { AbilityService, AddendumType } from '@zayan-erp/shared';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'deal-addendum-details',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    GenericDropdownComponent,
    ToggleSwitchModule,
    TextareaModule,
    SkeletonModule,
    MessageModule,
    DatePipe,
    CurrencyFormatPipe,
    NgbTooltipModule,
    BtnLoaderDirective
  ],
  providers: [],
  templateUrl: './deal-addendum-details.component.html'
})
export class DealAddendumDetailsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dealOptionsService = inject(DealOptionsService);
  private readonly fb = inject(FormBuilder);
  private readonly builderReferenceService = inject(BuilderReferenceService);
  private readonly userAuthService = inject(UserAuthenticationService);
  private readonly toaster = inject(ToastrService);
  private readonly abilityService = inject(AbilityService);
  /** Single source of truth for this component's write gate. */
  readonly canWriteDeals = computed(() => this.abilityService.ability.can('write-sales-deals', 'deals'));

  readonly AddendumType = AddendumType;
  readonly dealAddendum = input.required<DealOption>();
  readonly isUnderContract = input<boolean>(false);
  /** Set when the deal itself is cancelled: its addendums are read-only, exactly as a locked one is. */
  readonly dealCancelled = input<boolean>(false);

  // LOA addendums have no buyer, so buyer approval only applies to INI addendums.
  readonly hideBuyerApproved = computed(() => this.dealAddendum().addendumType.name !== AddendumType.INI);

  onChanges = output<void>();

  // Local writable signals for toggle states - synced with input but can be reverted on API failure
  readonly buyerApproved = linkedSignal(() => this.dealAddendum().buyerApproved);
  readonly builderApproved = linkedSignal(() => this.dealAddendum().builderApproved);
  readonly isLockedAndNotSuperAdmin = computed(() => this.dealAddendum().isLocked && !this.isSuperAdmin());
  readonly isLocked = computed(
    () => this.dealAddendum().isLocked || this.dealAddendum().isCancelled || this.dealCancelled()
  );
  readonly isSuperAdminUser = computed(() => this.isSuperAdmin());
  // Include in APS stays open on a locked addendum (a spec Schedule PE the deal took over), not on a cancelled one.
  readonly canChangeLockedIncludeInAps = computed(
    () =>
      this.canWriteDeals() && this.dealAddendum().isLocked && !this.dealAddendum().isCancelled && !this.dealCancelled()
  );

  isLoading = signal<boolean>(true);

  constructor() {
    // fake loading to show the skeleton
    toObservable(this.dealAddendum)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isLoading.set(true);
        setTimeout(() => {
          this.isLoading.set(false);
        }, 800);
      });
  }

  readonly builderReferences = toSignal(
    this.builderReferenceService
      .getBuilderReferences()
      .pipe(map(references => references.map<DropdownData>(ref => ({ id: ref.id, firstLabel: ref.reference })))),
    { initialValue: [] }
  );

  addendumForm = this.fb.group({
    builderReferenceId: new FormControl<string | null>(null),
    notes: new FormControl<string | null>(null),
    inContract: new FormControl<boolean>(false),
    cash: new FormControl<boolean>(false),
    includeInAps: new FormControl<boolean>(false)
  });

  onAddendumReceivedEffect = effect(() => {
    const addendum = this.dealAddendum();
    if (addendum) {
      this.addendumForm.patchValue({
        builderReferenceId: addendum.builderReference.id,
        notes: addendum.notes,
        inContract: addendum.inContract,
        cash: addendum.cash,
        includeInAps: addendum.includeInAps
      });

      if (this.isLocked() || !this.canWriteDeals()) {
        this.addendumForm.disable({ emitEvent: false });
        if (this.canChangeLockedIncludeInAps()) {
          this.addendumForm.controls.includeInAps.enable({ emitEvent: false });
        }
      } else {
        this.addendumForm.enable({ emitEvent: false });
      }
    }
  });

  updateAddendum(): void {
    if (!this.canWriteDeals()) return;
    if (this.isLocked()) {
      this.toaster.warning('Locked addendums cannot be modified.');
      return;
    }

    if (this.addendumForm.invalid) {
      this.addendumForm.markAllAsTouched();
      return;
    }

    const payload: UpdateDealAddendumPayload = this.addendumForm.getRawValue() as UpdateDealAddendumPayload;
    this.dealOptionsService
      .updateAddendum(this.dealAddendum().id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.onChanges.emit();
        }
      });
  }

  // A locked addendum has no Save: its Include in APS is sent on its own, and the server refuses it once the APS
  // has gone for signing. An open addendum keeps saving it with the rest of the details.
  updateLockedIncludeInAps(value: boolean): void {
    if (!this.canChangeLockedIncludeInAps()) return;

    const includeInApsControl = this.addendumForm.controls.includeInAps;
    this.dealOptionsService
      .updateAddendum(this.dealAddendum().id, { includeInAps: value })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.onChanges.emit();
        },
        error: () => {
          // Revert to original state on API failure
          includeInApsControl.setValue(!value, { emitEvent: false });
        }
      });
  }

  updateBuyerApproval(value: boolean): void {
    if (!this.canWriteDeals()) return;
    this.buyerApproved.set(value);

    if (this.isLockedAndNotSuperAdmin()) {
      this.toaster.warning('Locked addendums cannot be modified.');
      this.buyerApproved.set(!value);
      return;
    }

    if (value && !this.canApproveAddendum()) {
      this.buyerApproved.set(false);
      return;
    }

    // only super admins can revert the buyer approval
    const currentValue = this.dealAddendum().buyerApproved ?? false;
    const isReverting = currentValue && !value;
    if (isReverting && !this.isSuperAdmin()) {
      this.toaster.error('Only super admins can revert the buyer approval.', 'Permission Denied');

      setTimeout(() => {
        this.buyerApproved.set(!value);
      }, 500);
      return;
    }

    this.dealOptionsService.toggleApproval(this.dealAddendum().id, { toggleBuyerApproved: value }).subscribe({
      next: () => {
        this.onChanges.emit();
      },
      error: () => {
        // Revert to original state on API failure
        this.buyerApproved.set(!value);
      }
    });
  }

  updateBuilderApproval(value: boolean): void {
    if (!this.canWriteDeals()) return;
    this.builderApproved.set(value);

    if (this.isLockedAndNotSuperAdmin()) {
      this.toaster.warning('Locked addendums cannot be modified.');
      this.builderApproved.set(!value);
      return;
    }

    if (value && !this.canApproveAddendum()) {
      this.builderApproved.set(false);
      return;
    }

    // only super admins can revert the builder approval
    const currentValue = this.dealAddendum().builderApproved ?? false;
    const isReverting = currentValue && !value;
    if (isReverting && !this.isSuperAdmin()) {
      this.toaster.error('Only super admins can revert the builder approval.', 'Permission Denied');
      setTimeout(() => {
        this.builderApproved.set(!value);
      }, 500);
      return;
    }

    this.dealOptionsService
      .toggleApproval(this.dealAddendum().id, {
        toggleBuilderApproved: value
      })
      .subscribe({
        next: () => {
          this.onChanges.emit();
        },
        error: () => {
          // Revert to original state on API failure
          this.builderApproved.set(!value);
        }
      });
  }

  private canApproveAddendum(): boolean {
    if (this.dealAddendum().products.length === 0) {
      this.toaster.error('Cannot approve an empty addendum.');
      return false;
    }

    if (this.dealAddendum().builderApproved && !this.dealAddendum().builderApprovedDate) {
      this.toaster.error('Builder approval cannot occur without an approval date.');
      return false;
    }

    const hasMissingAttributeValues = this.dealAddendum().products.some(product => {
      if (!product.attributes?.length) {
        return false;
      }

      return product.attributes.some(attribute => !attribute.subCategoryAttributeValues?.length);
    });

    if (hasMissingAttributeValues) {
      this.toaster.error('Buyer or builder approval cannot occur while any option is missing an attribute value.');
      return false;
    }

    return true;
  }

  private isSuperAdmin(): boolean {
    const user = this.userAuthService.getUser();
    if (!user?.userModuleRoles) return false;

    const salesModuleRole = user.userModuleRoles.find((moduleRole: UserModuleRole) => {
      try {
        const moduleMetadata = JSON.parse(moduleRole.module.metadata);
        return moduleMetadata.label === 'Sales';
      } catch {
        return false;
      }
    });

    return salesModuleRole?.role?.roleName?.toLowerCase() === 'superadmin';
  }
}
